// `WordDocument` (SuiteImport/WordDocument.swift), ported step for step: a Word (.docx) file read as a run of paragraphs,
// each with the role its paragraph style gives it (heading level, title, block quote, scene break, body) and its text in
// the prose dialect. WordprocessingML is read directly, keeping style names; formatting the dialect can't express is
// reported, not silently dropped; comments and web links are marked with `AnchorSentinels` so the importer can turn them
// into margin comments (or links). The Swift streams each part through `XMLParser`; this walks `parseXML`'s tree with the
// same start/characters/end handlers, so the two read a file alike.
import type { Alignment, Inline, InkColor, InlineStyle, ListStyle } from '../contract.ts';
import { MAX_VERSE_INDENT, plainStyle } from '../proseMarkdown.ts';
import { graphemes, trimCFWhitespace, trimCFWhitespaceAndNewlines } from '../unicode.ts';
import { AnchorSentinels } from './anchorSentinels.ts';
import { isSceneBreakMarker } from './rtf.ts';
import { parseXML, type XMLNode } from './xmlTree.ts';

/** `verse`: a line of verse (`markVerse`), its indent steps in `level`; consecutive lines form a stanza. */
export type WordRole = 'body' | 'blockQuote' | 'verse' | 'sceneBreak' | 'heading' | 'title';

export type WordAnchor = { type: 'comment'; id: string } | { type: 'link'; url: string };

export interface WordParagraph {
  role: WordRole;
  /** With `heading`: the outline level, 0 for Heading 1; part-title styles sit above it at -1. */
  level: number | null;
  /** Formatted text, with anchor markers. */
  inlines: Inline[];
  /** The text without formatting or markers. */
  text: string;
  /** Pictures in the paragraph, as paths inside the package. */
  images: string[];
  styleName: string;
  /** Centred or right-aligned body text (justified is left as the format's: many manuscripts justify everything). */
  alignment: Alignment | null;
  /** A numbered or bulleted paragraph: its list style and the number Word shows. */
  listItem: { style: ListStyle; number: number } | null;
  /** The paragraph's left indent in twips, and its first line's offset from it (a hanging indent is negative). */
  leftIndent?: number;
  firstLineOffset?: number;
  /** An empty paragraph followed this one (a stanza's end, in verse). */
  blankAfter?: boolean;
}

export interface WordComment { author: string; date: Date | null; body: string }

export interface WordDocument {
  /** From the file's properties (docProps/core.xml). */
  title: string | null;
  author: string | null;
  created: Date | null;
  modified: Date | null;
  paragraphs: WordParagraph[];
  /** Footnote and endnote text by the id used in a footnote reference (endnotes are prefixed "e"). */
  footnotes: Map<string, Inline[]>;
  comments: Map<string, WordComment>;
  /** What each anchor marker's index refers to. */
  anchors: WordAnchor[];
  /** Picture bytes by package path. */
  media: Map<string, Uint8Array>;
  issues: string[];
}

/** `WordImportError`: the messages the Mac shows. */
export class WordImportError extends Error {
  constructor(readonly code: 'notAWordDocument' | 'legacyWordFormat') {
    super(code === 'legacyWordFormat'
      ? 'This is an older Word document (.doc). Open it in Word or Pages, save it as .docx, and import that.'
      : 'That isn’t a Word document (.docx).');
    this.name = 'WordImportError';
  }
}

/** A .docx's parts by path ("word/document.xml"), null when there's none. */
export type WordFiles = (path: string) => Uint8Array | null;

/** Whether the bytes are Word's older binary format (.doc): an OLE compound file. */
export function isLegacyWord(data: Uint8Array): boolean {
  return data.length >= 4 && data[0] === 0xd0 && data[1] === 0xcf && data[2] === 0x11 && data[3] === 0xe0;
}

/** `WordDocument.read`: the document from its parts (the app unzips the file; `isLegacyWord` refuses a .doc first). */
export function readWordDocument(files: WordFiles): WordDocument {
  const body = files('word/document.xml');
  if (!body) throw new WordImportError('notAWordDocument');
  const xml = (path: string): XMLNode | null => {
    const data = files(path);
    if (!data) return null;
    try { return parseXML(normaliseLineEnds(data)); } catch { return null; }
  };
  const styles = new StyleSheet(xml('word/styles.xml'));
  const rels = relationships(xml('word/_rels/document.xml.rels'));
  const shared = new SharedState();

  let root: XMLNode;
  try { root = parseXML(normaliseLineEnds(body)); } catch { throw new WordImportError('notAWordDocument'); }
  const parser = new BodyParser(styles, rels, shared, new WordNumbering(xml('word/numbering.xml')), null);
  parser.parse(root);
  for (const [part, prefix] of [['footnotes', ''], ['endnotes', 'e']] as const) {
    const notes = xml(`word/${part}.xml`);
    if (!notes) continue;
    const noteParser = new BodyParser(styles, relationships(xml(`word/_rels/${part}.xml.rels`)), shared, new WordNumbering(null), prefix);
    noteParser.parse(notes);
    for (const [id, inlines] of noteParser.notes) if (!shared.footnotes.has(id)) shared.footnotes.set(id, inlines);
  }

  const paragraphs = parser.paragraphs;
  markVerse(paragraphs, shared.issues);
  const media = new Map<string, Uint8Array>();
  for (const path of new Set(paragraphs.flatMap((p) => p.images))) {
    const bytes = files(path);
    if (bytes) media.set(path, bytes);
  }
  const core = xml('docProps/core.xml');
  const coreValue = (name: string): string | null => {
    const v = core?.child(name) ? trimCFWhitespaceAndNewlines(core.child(name)!.text) : null;
    return v ? v : null;
  };
  return {
    title: coreValue('dc:title'), author: coreValue('dc:creator'),
    created: iso8601(coreValue('dcterms:created')), modified: iso8601(coreValue('dcterms:modified')),
    paragraphs, footnotes: shared.footnotes,
    comments: readComments(xml('word/comments.xml')), anchors: shared.anchors,
    media, issues: shared.issues.messages,
  };
}

/** XML's own end-of-line handling, which the Mac's parser does: CR LF and lone CRs read as LF. */
function normaliseLineEnds(data: Uint8Array): string {
  return new TextDecoder('utf-8').decode(data).replace(/\r\n?/g, '\n');
}

/** `Date(_:strategy: .iso8601)`: a date and time with Z or an offset (Word writes fractions of a second too). */
export function iso8601(s: string | null | undefined): Date | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})$/.test(s)) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `word/comments.xml`: each comment's author, date and text (paragraphs on separate lines). */
function readComments(root: XMLNode | null): Map<string, WordComment> {
  const comments = new Map<string, WordComment>();
  if (!root) return comments;
  const paragraphs = (node: XMLNode): XMLNode[] => (node.name === 'w:p' ? [node] : node.children.flatMap(paragraphs));
  const text = (node: XMLNode): string => (node.name === 'w:t' ? node.text : node.children.map(text).join(''));
  for (const c of root.childrenNamed('w:comment')) {
    const id = c.attr('w:id');
    if (id === undefined) continue;
    const body = trimCFWhitespaceAndNewlines(paragraphs(c).map(text).join('\n'));
    comments.set(id, { author: c.attr('w:author') ?? 'Reviewer', date: iso8601(c.attr('w:date')), body });
  }
  return comments;
}

/** Swift's `Int(_:)`: an optional sign and ASCII digits, nothing else. */
const int = (s: string | undefined): number | null => (s !== undefined && /^[+-]?\d+$/.test(s) ? Number(s) : null);

// MARK: Styles

/** Character formatting; null means "not said here", so layers can be overlaid. */
interface RunFormat {
  italic: boolean | null;
  bold: boolean | null;
  smallCaps: boolean | null;
  caps: boolean | null;
  underline: boolean | null;
  strike: boolean | null;
  raised: boolean | null;
  /** Word's highlight colour name ("yellow", "darkCyan"; "none" when turned off). */
  highlight: string | null;
}

const RUN_KEYS = ['italic', 'bold', 'smallCaps', 'caps', 'underline', 'strike', 'raised', 'highlight'] as const;
const emptyRun = (): RunFormat => ({ italic: null, bold: null, smallCaps: null, caps: null, underline: null, strike: null, raised: null, highlight: null });

function overlayRun(base: RunFormat, other: RunFormat) {
  for (const key of RUN_KEYS) if (other[key] !== null) (base as unknown as Record<string, unknown>)[key] = other[key];
}

/** One child of a `w:rPr`. */
function readRun(format: RunFormat, name: string, attributes: Record<string, string>) {
  const value = attributes['w:val'];
  const on = value === undefined ? true : !['0', 'false', 'off', 'none'].includes(value);
  switch (name) {
    case 'w:i': format.italic = on; break;
    case 'w:b': format.bold = on; break;
    case 'w:smallCaps': format.smallCaps = on; break;
    case 'w:caps': format.caps = on; break;
    case 'w:u': format.underline = on; break;
    case 'w:strike': case 'w:dstrike': format.strike = on; break;
    case 'w:vertAlign': format.raised = value === 'superscript' || value === 'subscript'; break;
    case 'w:highlight': format.highlight = value ?? 'yellow'; break;
  }
}

interface ParagraphFormat {
  outlineLevel: number | null;
  alignment: string | null;
  left: number | null;
  firstLine: number | null;
  hanging: number | null;
  numbered: boolean | null;
  /** `w:numPr`'s list (`w:numId`, "0" for none) and level. */
  numID: string | null;
  level: number | null;
}

const PARAGRAPH_KEYS = ['outlineLevel', 'alignment', 'left', 'firstLine', 'hanging', 'numbered', 'numID', 'level'] as const;
const emptyParagraph = (): ParagraphFormat => ({ outlineLevel: null, alignment: null, left: null, firstLine: null, hanging: null, numbered: null, numID: null, level: null });

function overlayParagraph(base: ParagraphFormat, other: ParagraphFormat) {
  for (const key of PARAGRAPH_KEYS) if (other[key] !== null) (base as unknown as Record<string, unknown>)[key] = other[key];
}

/** One child of a `w:numPr`. */
function readNumbering(format: ParagraphFormat, name: string, attributes: Record<string, string>) {
  switch (name) {
    case 'w:numId': format.numID = attributes['w:val'] ?? null; break;
    case 'w:ilvl': format.level = int(attributes['w:val']); break;
  }
}

/** One child of a `w:pPr`. */
function readParagraph(format: ParagraphFormat, name: string, attributes: Record<string, string>) {
  switch (name) {
    case 'w:outlineLvl': format.outlineLevel = int(attributes['w:val']); break;
    case 'w:jc': format.alignment = attributes['w:val'] ?? null; break;
    case 'w:ind': {
      const left = int(attributes['w:left'] ?? attributes['w:start']);
      if (left !== null) format.left = left;
      const first = int(attributes['w:firstLine']);
      if (first !== null) { format.firstLine = first; format.hanging = 0; }
      const hanging = int(attributes['w:hanging']);
      if (hanging !== null) { format.hanging = hanging; format.firstLine = 0; }
      break;
    }
    case 'w:numPr': format.numbered = true; break;
  }
}

interface Style { name: string; basedOn: string | null; paragraph: ParagraphFormat; run: RunFormat }

/** `word/styles.xml`, resolved through `basedOn`. */
class StyleSheet {
  private readonly styles = new Map<string, Style>();
  readonly defaultParagraphStyle: string | null = null;

  constructor(root: XMLNode | null) {
    if (!root) return;
    for (const node of root.childrenNamed('w:style')) {
      const id = node.attr('w:styleId');
      if (id === undefined) continue;
      const style: Style = { name: node.child('w:name')?.attr('w:val') ?? id, basedOn: node.child('w:basedOn')?.attr('w:val') ?? null, paragraph: emptyParagraph(), run: emptyRun() };
      for (const p of node.child('w:pPr')?.children ?? []) readParagraph(style.paragraph, p.name, p.attributes);
      for (const n of node.child('w:pPr')?.child('w:numPr')?.children ?? []) readNumbering(style.paragraph, n.name, n.attributes);
      for (const r of node.child('w:rPr')?.children ?? []) readRun(style.run, r.name, r.attributes);
      this.styles.set(id, style);
      if (node.attr('w:type') === 'paragraph' && node.attr('w:default') === '1') (this as { defaultParagraphStyle: string | null }).defaultParagraphStyle = id;
    }
  }

  name(id: string | null): string {
    return (id !== null ? this.styles.get(id)?.name : undefined) ?? 'Normal';
  }

  private chain(id: string | null): Style[] {
    const result: Style[] = [];
    let next = id;
    while (next !== null && result.length < 20) {
      const style = this.styles.get(next);
      if (!style) break;
      result.unshift(style);
      next = style.basedOn;
    }
    return result;
  }

  paragraphFormat(id: string | null): ParagraphFormat {
    const format = emptyParagraph();
    for (const style of this.chain(id)) overlayParagraph(format, style.paragraph);
    return format;
  }

  runFormat(id: string | null): RunFormat {
    const format = emptyRun();
    for (const style of this.chain(id)) overlayRun(format, style.run);
    return format;
  }
}

interface Level { format: string; text: string; start: number }

/** `word/numbering.xml`: each list's levels, as the dialect's list styles. */
export class WordNumbering {
  /** By `w:numId`: its levels, with the list's own start overrides applied. */
  private readonly lists = new Map<string, Map<number, Level>>();

  constructor(root: XMLNode | null) {
    if (!root) return;
    const levels = (node: XMLNode): Map<number, Level> => {
      const result = new Map<number, Level>();
      for (const lvl of node.childrenNamed('w:lvl')) {
        const n = int(lvl.attr('w:ilvl'));
        if (n === null) continue;
        result.set(n, { format: lvl.child('w:numFmt')?.attr('w:val') ?? 'decimal', text: lvl.child('w:lvlText')?.attr('w:val') ?? '',
          start: int(lvl.child('w:start')?.attr('w:val')) ?? 1 });
      }
      return result;
    };
    const abstract = new Map<string, Map<number, Level>>();
    for (const node of root.childrenNamed('w:abstractNum')) {
      const id = node.attr('w:abstractNumId');
      if (id !== undefined) abstract.set(id, levels(node));
    }
    for (const num of root.childrenNamed('w:num')) {
      const id = num.attr('w:numId');
      const base = num.child('w:abstractNumId')?.attr('w:val');
      if (id === undefined || base === undefined) continue;
      const list = new Map(abstract.get(base) ?? []);
      for (const override of num.childrenNamed('w:lvlOverride')) {
        const n = int(override.attr('w:ilvl'));
        if (n === null) continue;
        const redefined = levels(override).get(n);
        if (redefined) list.set(n, redefined);
        const start = int(override.child('w:startOverride')?.attr('w:val'));
        if (start !== null) list.set(n, { ...(list.get(n) ?? { format: 'decimal', text: '', start: 1 }), start });
      }
      this.lists.set(id, list);
    }
  }

  level(numID: string, level: number): Level | null { return this.lists.get(numID)?.get(level) ?? null; }

  /** The dialect's style for a level: bullets by their glyph, numbers by their format (null for `none`: no marker). */
  static style(level: Level | null): ListStyle | null {
    if (!level) return 'bullet';
    switch (level.format) {
      case 'none': return null;
      case 'bullet':
        switch (trimCFWhitespace(level.text)) {
          case '◦': case 'o': case '○': case String.fromCharCode(0xf06f): return 'circle';
          case '–': case '-': case '—': case '−': case '⁃': case String.fromCharCode(0xf02d): return 'dash';
          default: return 'bullet';
        }
      case 'lowerLetter': return 'lowerAlpha';
      case 'upperLetter': return 'upperAlpha';
      case 'lowerRoman': return 'lowerRoman';
      case 'upperRoman': return 'upperRoman';
      default: return level.text.endsWith(')') ? 'decimalParen' : 'decimal';
    }
  }
}

/** A part's relationships: id → target (package path, or URL when external). */
type Relationships = Map<string, { target: string; external: boolean }>;

function relationships(root: XMLNode | null): Relationships {
  const targets: Relationships = new Map();
  if (!root) return targets;
  for (const r of root.childrenNamed('Relationship')) {
    const id = r.attr('Id'), target = r.attr('Target');
    if (id === undefined || target === undefined) continue;
    if (r.attr('TargetMode') === 'External') targets.set(id, { target, external: true });
    // Package paths are relative to word/ unless absolute.
    else targets.set(id, { target: target.startsWith('/') ? target.slice(1) : 'word/' + target, external: false });
  }
  return targets;
}

/** `IssueCounter`: each message once, with "(×n)" when it happened more than once. */
class IssueCounter {
  private readonly counts = new Map<string, number>();
  add(message: string) { this.counts.set(message, (this.counts.get(message) ?? 0) + 1); }
  get messages(): string[] { return [...this.counts].map(([m, n]) => (n > 1 ? `${m} (×${n})` : m)); }
}

/** State shared by the body and notes parsers: anchors are numbered across the whole document. */
class SharedState {
  anchors: WordAnchor[] = [];
  commentAnchors = new Map<string, number>();
  footnotes = new Map<string, Inline[]>();
  issues = new IssueCounter();

  commentAnchor(id: string): number {
    const known = this.commentAnchors.get(id);
    if (known !== undefined) return known;
    this.anchors.push({ type: 'comment', id });
    this.commentAnchors.set(id, this.anchors.length - 1);
    return this.anchors.length - 1;
  }

  linkAnchor(url: string): number {
    this.anchors.push({ type: 'link', url });
    return this.anchors.length - 1;
  }
}

// MARK: Body

type Piece =
  | { type: 'text'; text: string; style: InlineStyle }
  | { type: 'lineBreak' }
  | { type: 'footnote'; id: string }
  | { type: 'anchorStart'; index: number }
  | { type: 'anchorEnd'; index: number };

class Builder {
  pieces: Piece[] = [];
  styleID: string | null = null;
  direct = emptyParagraph();
  images: string[] = [];
}

const styleOf = (piece: Piece): InlineStyle | null => (piece.type === 'text' ? piece.style : null);

/** The nearest of the writer's highlights to one of Word's (none for black, white and greys). */
export function wordHighlightInk(word: string): InkColor | null {
  switch (word) {
    case 'yellow': case 'darkYellow': return 'yellow';
    case 'green': case 'darkGreen': return 'green';
    case 'cyan': case 'blue': case 'darkBlue': case 'darkCyan': return 'blue';
    case 'magenta': case 'red': case 'darkRed': return 'pink';
    case 'darkMagenta': return 'purple';
    default: return null;
  }
}

/** Style names that mean verse, wherever they appear in the name (`WordDocument.verseStyleWords`). */
export const VERSE_STYLE_WORDS = ['poem', 'verse', 'poetry', 'stanza', 'haiku', 'lyric'];

/** `WordDocument.markVerse`: marks the paragraphs that are lines of verse — by style name, or by shape: a run of three
 *  or more body paragraphs that each hold line breaks, or whose left indents step (two or more distinct indents, none
 *  with a first-line indent of its own, every line at most 70 characters, and no quotation style among them) — never by
 *  shortness alone, which is dialogue more often than verse. A line's steps are its left indent over the run's smallest
 *  non-zero one, six at most. Such a run leaves `isQuote`'s block quotation behind. */
export function markVerse(paragraphs: WordParagraph[], issues: { add(message: string): void }): void {
  let poems = 0;
  const candidate = (p: WordParagraph) => (p.role === 'body' || p.role === 'blockQuote') && p.listItem === null && p.text !== '' && p.alignment === null;
  const styled = (p: WordParagraph) => {
    const name = p.styleName.toLowerCase();
    return VERSE_STYLE_WORDS.some((w) => name.includes(w)) || name.startsWith('verse indent');
  };
  const isVerse = (p: WordParagraph) => p.role === 'verse';
  const short = (p: WordParagraph) => p.text.split('\n').filter((l) => l !== '').every((l) => graphemes(l).length <= 70);
  const quoteStyled = (p: WordParagraph) => ['quote', 'extract', 'block text'].some((w) => p.styleName.toLowerCase().includes(w));
  // Each rule's runs: consecutive paragraphs it applies to (an empty paragraph, already dropped, never parts them).
  const mark = (applies: (p: WordParagraph) => boolean, minimum: number, qualifies: (run: WordParagraph[]) => boolean) => {
    let i = 0;
    while (i < paragraphs.length) {
      if (!applies(paragraphs[i])) { i++; continue; }
      let j = i;
      while (j < paragraphs.length && applies(paragraphs[j])) j++;
      const run = paragraphs.slice(i, j);
      if (run.length >= minimum && qualifies(run)) {
        const lefts = run.map((p) => p.leftIndent ?? 0).filter((l) => l > 0);
        const step = lefts.length ? Math.min(...lefts) : 0;
        for (let k = i; k < j; k++) {
          if (paragraphs[k].role === 'verse' && paragraphs[k].level === 0) continue;
          paragraphs[k].role = 'verse';
          paragraphs[k].level = step > 0 ? Math.min(MAX_VERSE_INDENT, Math.round((paragraphs[k].leftIndent ?? 0) / step)) : 0;
        }
        poems++;
      }
      i = j;
    }
  };
  mark((p) => candidate(p) && styled(p), 1, () => true);
  mark((p) => candidate(p) && p.inlines.some((x) => x.type === 'lineBreak') && !isVerse(p), 3, () => true);
  mark((p) => candidate(p) && short(p) && (p.firstLineOffset ?? 0) === 0 && !quoteStyled(p) && !isVerse(p), 3,
    (run) => new Set(run.map((p) => p.leftIndent ?? 0)).size >= 2);
  if (poems > 0) issues.add('Poems set as verse, their lines stepped by their indents');
}

/** Reads a WordprocessingML part (the body, footnotes or endnotes) into paragraphs. */
class BodyParser {
  /** Each list's count so far, by `w:numId` and level. */
  private readonly counters = new Map<string, Map<number, number>>();
  readonly paragraphs: WordParagraph[] = [];
  readonly notes = new Map<string, Inline[]>();

  private readonly path: string[] = [];
  private readonly builders: Builder[] = [];
  /** Paragraphs of text boxes, placed after the paragraph holding them. */
  private nested: WordParagraph[] = [];
  /** Anchors that start between paragraphs, carried into the next one. */
  private pendingStarts: number[] = [];
  private runDirect = emptyRun();
  private runStyle: string | null = null;
  private inText = false;
  /** Inside deleted or moved-away text. */
  private skipDepth = 0;
  /** Inside a fallback picture. */
  private fallbackDepth = 0;
  private readonly links: (number | null)[] = [];
  private note: string | null = null;
  private noteParagraphs: WordParagraph[] = [];
  private trackedChanges = 0;

  constructor(
    private readonly styles: StyleSheet,
    private readonly rels: Relationships,
    private readonly shared: SharedState,
    private readonly numbering: WordNumbering,
    /** Set when parsing footnotes ("") or endnotes ("e"). */
    private readonly notePrefix: string | null,
  ) {}

  parse(root: XMLNode) {
    const walk = (node: XMLNode) => {
      this.start(node.name, node.attributes);
      if (this.inText && this.fallbackDepth === 0 && node.text) this.append(node.text);
      for (const child of node.children) walk(child);
      this.end(node.name);
    };
    walk(root);
    if (this.trackedChanges > 0) this.shared.issues.add(`Tracked changes accepted (${this.trackedChanges} insertions and deletions)`);
  }

  private get parent(): string | null { return this.path.length >= 2 ? this.path[this.path.length - 2] : null; }
  private get grandparent(): string | null { return this.path.length >= 3 ? this.path[this.path.length - 3] : null; }

  private start(name: string, attributes: Record<string, string>) {
    this.path.push(name);
    if (name === 'mc:Fallback') this.fallbackDepth += 1;
    if (this.fallbackDepth !== 0) return;
    const builder = this.builders[this.builders.length - 1] as Builder | undefined;
    const { parent, grandparent } = this;

    if (name === 'w:footnote' || name === 'w:endnote') {
      const id = attributes['w:id'];
      if (this.notePrefix !== null && id !== undefined && !(attributes['w:type'] ?? '').toLowerCase().includes('separator')
        && attributes['w:type'] !== 'continuationNotice') {
        this.note = this.notePrefix + id;
        this.noteParagraphs = [];
      }
    } else if (name === 'w:p') {
      const b = new Builder();
      b.pieces = this.pendingStarts.map((index) => ({ type: 'anchorStart', index }));
      this.pendingStarts = [];
      this.builders.push(b);
    } else if (name === 'w:pStyle' && parent === 'w:pPr' && grandparent === 'w:p') {
      if (builder) builder.styleID = attributes['w:val'] ?? null;
    } else if (parent === 'w:numPr' && grandparent === 'w:pPr') {
      if (builder) readNumbering(builder.direct, name, attributes);
    } else if (parent === 'w:pPr' && grandparent === 'w:p') {
      if (builder) readParagraph(builder.direct, name, attributes);
    } else if (name === 'w:r') {
      this.runDirect = emptyRun();
      this.runStyle = null;
    } else if (name === 'w:rStyle' && parent === 'w:rPr' && grandparent === 'w:r') {
      this.runStyle = attributes['w:val'] ?? null;
    } else if (parent === 'w:rPr' && grandparent === 'w:r') {
      readRun(this.runDirect, name, attributes);
    } else {
      switch (name) {
        case 'w:ins': case 'w:moveTo':
          this.trackedChanges += 1;
          break;
        case 'w:del': case 'w:moveFrom':
          this.trackedChanges += 1;
          this.skipDepth += 1;
          break;
        case 'w:t':
          if (parent === 'w:r') this.inText = this.skipDepth === 0;
          break;
        case 'w:tab':
          if (parent === 'w:r') this.append(' ');
          break;
        case 'w:br': case 'w:cr':
          if (parent === 'w:r' && this.skipDepth === 0 && attributes['w:type'] !== 'page' && attributes['w:type'] !== 'column') builder?.pieces.push({ type: 'lineBreak' });
          break;
        case 'w:noBreakHyphen':
          this.append('-');
          break;
        case 'w:sym':
          if (this.skipDepth === 0) this.shared.issues.add('Symbol-font characters left out');
          break;
        case 'w:footnoteReference': case 'w:endnoteReference': {
          const id = attributes['w:id'];
          if (this.skipDepth === 0 && id !== undefined) builder?.pieces.push({ type: 'footnote', id: (name === 'w:endnoteReference' ? 'e' : '') + id });
          break;
        }
        case 'w:hyperlink': {
          const id = attributes['r:id'];
          const target = id !== undefined ? this.rels.get(id) : undefined;
          if (target?.external) {
            const index = this.shared.linkAnchor(target.target);
            this.links.push(index);
            builder?.pieces.push({ type: 'anchorStart', index });
          } else {
            this.links.push(null);
          }
          break;
        }
        case 'w:commentRangeStart': {
          const id = attributes['w:id'];
          if (id === undefined) break;
          const index = this.shared.commentAnchor(id);
          if (builder) builder.pieces.push({ type: 'anchorStart', index });
          else this.pendingStarts.push(index);
          break;
        }
        case 'w:commentRangeEnd': {
          const id = attributes['w:id'];
          const index = id !== undefined ? this.shared.commentAnchors.get(id) : undefined;
          if (index === undefined) break;
          if (builder) builder.pieces.push({ type: 'anchorEnd', index });
          else this.closeAfterLastParagraph(index);
          break;
        }
        case 'w:commentReference': {
          // A comment on a point, with no range: it covers its paragraph so far.
          const id = attributes['w:id'];
          if (id === undefined || this.shared.commentAnchors.has(id) || !builder) break;
          const index = this.shared.commentAnchor(id);
          builder.pieces.unshift({ type: 'anchorStart', index });
          builder.pieces.push({ type: 'anchorEnd', index });
          break;
        }
        case 'a:blip': {
          const target = attributes['r:embed'] !== undefined ? this.rels.get(attributes['r:embed']) : undefined;
          if (target && !target.external) builder?.images.push(target.target);
          break;
        }
        case 'v:imagedata': {
          const target = attributes['r:id'] !== undefined ? this.rels.get(attributes['r:id']) : undefined;
          if (target && !target.external) builder?.images.push(target.target);
          break;
        }
        case 'w:tbl':
          this.shared.issues.add('Table flattened into paragraphs');
          break;
        case 'w:txbxContent':
          this.shared.issues.add('Text box text placed after the paragraph holding it');
          break;
      }
    }
  }

  private end(name: string) {
    try {
      if (this.fallbackDepth !== 0) return;
      switch (name) {
        case 'w:t': this.inText = false; break;
        case 'w:del': case 'w:moveFrom': this.skipDepth -= 1; break;
        case 'w:hyperlink': {
          const index = this.links.pop();
          if (index !== undefined && index !== null) this.builders[this.builders.length - 1]?.pieces.push({ type: 'anchorEnd', index });
          break;
        }
        case 'w:p': {
          const builder = this.builders.pop();
          if (!builder) break;
          const finished = this.finish(builder);
          if (this.builders.length) {
            if (finished) this.nested.push(finished);
          } else {
            if (finished) this.emit(finished);
            for (const p of this.nested) this.emit(p);
            this.nested = [];
          }
          break;
        }
        case 'w:footnote': case 'w:endnote':
          if (this.note !== null) {
            const inlines: Inline[] = [];
            this.noteParagraphs.forEach((p, n) => {
              if (n > 0) inlines.push({ type: 'lineBreak' });
              inlines.push(...p.inlines);
            });
            this.notes.set(this.note, inlines);
          }
          this.note = null;
          break;
      }
    } finally {
      this.path.pop();
      if (name === 'mc:Fallback') this.fallbackDepth -= 1;
    }
  }

  private emit(paragraph: WordParagraph) {
    if (this.notePrefix !== null) {
      if (this.note !== null) this.noteParagraphs.push(paragraph);
    } else {
      this.paragraphs.push(paragraph);
    }
  }

  private append(text: string) {
    const builder = this.builders[this.builders.length - 1];
    if (this.skipDepth !== 0 || !builder) return;
    const format = this.styles.runFormat(builder.styleID ?? this.styles.defaultParagraphStyle);
    overlayRun(format, this.styles.runFormat(this.runStyle));
    overlayRun(format, this.runDirect);
    const style = plainStyle();
    if (format.italic === true) style.italic = true;
    if (format.bold === true) style.bold = true;
    if (format.smallCaps === true) style.smallCaps = true;
    if (format.underline === true) style.underline = true;
    const visible = trimCFWhitespace(text) !== '';
    if (format.highlight !== null && format.highlight !== 'none') {
      style.highlight = wordHighlightInk(format.highlight);
      if (visible && style.highlight === null) this.shared.issues.add('Highlighting in black, white or grey removed (text kept)');
    }
    if (visible) {
      if (format.strike === true) this.shared.issues.add('Strikethrough removed (text kept)');
      if (format.raised === true) this.shared.issues.add('Superscript or subscript removed (text kept)');
      if (format.caps === true) this.shared.issues.add('All-caps formatting removed (text kept as typed)');
    }
    builder.pieces.push({ type: 'text', text: AnchorSentinels.neutralise(text.replaceAll('\t', ' ')), style });
  }

  /** An anchor ending between paragraphs closes at the end of the one before. */
  private closeAfterLastParagraph(index: number) {
    if (this.notePrefix === null && this.paragraphs.length) {
      this.paragraphs[this.paragraphs.length - 1].inlines.push({ type: 'text', text: AnchorSentinels.marker('end', index), style: plainStyle() });
    }
  }

  // MARK: Paragraph roles

  private finish(builder: Builder): WordParagraph | null {
    const pieces = builder.pieces;
    trimSpaces(pieces, true);
    trimSpaces(pieces, false);
    const text = pieces.map((p) => (p.type === 'text' ? p.text : p.type === 'lineBreak' ? '\n' : '')).join('');
    const trimmed = trimCFWhitespaceAndNewlines(text);

    const format = this.styles.paragraphFormat(builder.styleID ?? this.styles.defaultParagraphStyle);
    overlayParagraph(format, builder.direct);
    const styleName = this.styles.name(builder.styleID ?? this.styles.defaultParagraphStyle);
    const name = styleName.toLowerCase();

    if (name.startsWith('toc ') || name === 'toc heading' || name.startsWith('index ') || name === 'table of figures') {
      if (trimmed) this.shared.issues.add('Table of contents or index left out (compile makes a contents page)');
      this.carryAnchors(pieces);
      return null;
    }

    let role: WordRole = 'body';
    let level: number | null = null;
    const heading = /^heading ([1-9])$/.exec(name);
    if (format.outlineLevel !== null && format.outlineLevel < 9) {
      role = 'heading'; level = format.outlineLevel;
    } else if (heading) {
      role = 'heading'; level = Number(heading[1]) - 1;
    } else if (name === 'title' || name.endsWith('book title')) {
      role = 'title';
    } else if (['part title', 'part heading', 'part name'].some((n) => name.includes(n))) {
      role = 'heading'; level = -1;
    } else if (['chapter title', 'chapter heading', 'chapter name'].some((n) => name.includes(n))) {
      role = 'heading'; level = 0;
    } else if (isSceneBreakMarker(trimmed)) {
      role = 'sceneBreak';
    } else if (isQuote(name, format)) {
      role = 'blockQuote';
    }
    if (!trimmed && (!builder.images.length || role !== 'body')) {
      this.carryAnchors(pieces);
      if (this.notePrefix === null && this.paragraphs.length) this.paragraphs[this.paragraphs.length - 1].blankAfter = true;
      return null;
    }
    let listItem: { style: ListStyle; number: number } | null = null;
    if (role === 'body' && format.numID !== null && format.numID !== '0' && this.notePrefix === null) {
      const lvl = Math.max(0, Math.min(format.level ?? 0, 8));
      const definition = this.numbering.level(format.numID, lvl);
      const style = WordNumbering.style(definition);
      if (style) {
        // Word counts each list's levels; a level's count restarts after an item above it.
        const counts = this.counters.get(format.numID) ?? new Map<number, number>();
        const number = (counts.get(lvl) ?? (definition?.start ?? 1) - 1) + 1;
        counts.set(lvl, number);
        for (const deeper of [...counts.keys()]) if (deeper > lvl) counts.delete(deeper);
        this.counters.set(format.numID, counts);
        listItem = { style, number };
        if (lvl > 0) this.shared.issues.add('Lists inside lists set as one level');
      }
    }
    let alignment: Alignment | null = null;
    if (role === 'body' && !listItem) {
      alignment = format.alignment === 'center' ? 'center' : format.alignment === 'right' || format.alignment === 'end' ? 'right' : null;
    }

    const inlines: Inline[] = [];
    pieces.forEach((piece, i) => {
      switch (piece.type) {
        case 'text': inlines.push({ type: 'text', text: piece.text, style: piece.style }); break;
        case 'lineBreak': inlines.push({ type: 'lineBreak' }); break;
        case 'footnote': inlines.push({ type: 'footnoteReference', id: piece.id }); break;
        case 'anchorStart': case 'anchorEnd': {
          // Markers take a neighbour's style so they don't break an emphasis run.
          let before: InlineStyle | null = null;
          for (let k = i - 1; k >= 0 && before === null; k--) before = styleOf(pieces[k]);
          let neighbour = before;
          for (let k = i + 1; k < pieces.length && neighbour === null; k++) neighbour = styleOf(pieces[k]);
          inlines.push({ type: 'text', text: AnchorSentinels.marker(piece.type === 'anchorStart' ? 'start' : 'end', piece.index), style: neighbour ?? plainStyle() });
          break;
        }
      }
    });
    return { role, level, inlines, text: trimmed, images: builder.images, styleName, alignment, listItem,
      leftIndent: format.left ?? 0, firstLineOffset: (format.firstLine ?? 0) - (format.hanging ?? 0) };
  }

  /** Anchors in a paragraph that isn't kept move to its neighbours. */
  private carryAnchors(pieces: Piece[]) {
    for (const piece of pieces) {
      if (piece.type === 'anchorStart') this.pendingStarts.push(piece.index);
      else if (piece.type === 'anchorEnd') {
        const i = this.pendingStarts.indexOf(piece.index);
        if (i >= 0) this.pendingStarts.splice(i, 1);
        else this.closeAfterLastParagraph(piece.index);
      }
    }
  }
}

function isQuote(name: string, format: ParagraphFormat): boolean {
  if (name.includes('list') || format.numbered === true) return false;
  if ((name.includes('quote') && !name.includes('char')) || name === 'block text' || name.includes('extract')) return true;
  if (format.alignment === 'center' || format.alignment === 'right' || format.alignment === 'end') return false;
  const left = format.left ?? 0;
  const first = left + (format.firstLine ?? 0) - (format.hanging ?? 0);
  return left >= 600 && first >= 600;
}

/** Spaces off the paragraph's start or end, past anchor markers; a line break or footnote stops it. */
function trimSpaces(pieces: Piece[], leading: boolean) {
  let i = leading ? 0 : pieces.length - 1;
  while (i >= 0 && i < pieces.length) {
    const piece = pieces[i];
    switch (piece.type) {
      case 'text': {
        const trimmed = leading ? piece.text.replace(/^ +/, '') : piece.text.replace(/ +$/, '');
        if (!trimmed) {
          pieces.splice(i, 1);
          if (!leading) i -= 1;
          continue;
        }
        pieces[i] = { ...piece, text: trimmed };
        return;
      }
      case 'anchorStart': case 'anchorEnd':
        i += leading ? 1 : -1;
        break;
      default:
        return;
    }
  }
}
